import { FormControl, FormLabel } from '@mui/material';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import { getIn } from 'formik';
import React, { HTMLInputTypeAttribute } from 'react';
import { mascaraCep } from 'utils/format_cep';
import { mascaraCnpj, mascaraCpf, mascaraCpfCnpj } from 'utils/format_cpf';
import { monetario_input, monetario_output } from 'utils/format_monetario';
import { mascaraTelefone } from 'utils/format_phone';

interface InputComLabelProps {
  formik: any;
  label: string;
  labelTexto: string;
  placeholder?: string;
  width?: string;
  type?: HTMLInputTypeAttribute;
  required?: boolean;
  maxLength?: number;
  acceptFraction?: boolean;
  version?: 'primary' | 'secondary';
  mask?: 'cep' | 'telefone' | 'numero' | 'porcentagem' | 'dinheiro' | 'numerov2' | 'cpf' | 'cnpj' | 'cpfcnpj' | 'custom' | null;
  customMask?: (value: string) => string;
  disabled?: boolean;
}

const mascaraPorcentagem = (valor: string) => {
  if (valor.length === 0) return '';
  const alterado = valor.replace('%', '');
  if (isNaN(Number(alterado))) return '';
  if (Number(alterado) > 100) return '100%';
  if (Number(alterado) < 0 || Number(alterado) === 0) return '0%';
  return alterado + '%';
};

const InputComLabelFormik: React.FC<InputComLabelProps> = ({
  formik,
  label,
  labelTexto,
  placeholder = '',
  width = '100%',
  type = 'text',
  maxLength = 200,
  required = false,
  version = 'secondary',
  mask = null,
  disabled = false,
  acceptFraction = true,
  customMask = null,
}) => {

  // Função para obter o valor formatado para exibição
  const getDisplayValue = () => {

    const currentValue = getIn(formik.values, label);

    if (mask === 'dinheiro') {
      // Se for number, formata para exibição
      if (typeof currentValue === 'number') {
        return monetario_output(currentValue);
      }
      // Se for string vazia ou undefined, retorna vazio
      if (!currentValue) return '';
      // Se for string, já está formatada
      return currentValue;
    }

    if (mask === 'porcentagem') {
      return mascaraPorcentagem(currentValue || '');
    }

    if (mask === 'numero') {
      if (!acceptFraction) {
        return Math.floor(Number(currentValue)) || 0;
      }
      return Number(currentValue) || 0;
    }

    if (mask === 'numerov2') {
      if (!acceptFraction) {
        return Math.floor(Number(currentValue)) || 0;
      }
      return currentValue || '';
    }

    if (mask === 'cpf') {
      return mascaraCpf(currentValue || '');
    }

    if (mask === 'cnpj') {
      return mascaraCnpj(currentValue || '');
    }

    if (mask === 'cpfcnpj') {
      return mascaraCpfCnpj(currentValue || '');
    }

    return currentValue || '';
  };

  // Rótulo que será exibido acima do campo
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {

    let value: string | number = event.target.value;

    // Validação geral para acceptFraction quando type é number ou mask é numero/numerov2
    if ((type === 'number' || mask === 'numero' || mask === 'numerov2') && !acceptFraction) {
      const regex = /^[0-9]*$/;
      if (!regex.test(value as string)) {
        // Mantém o valor anterior do formik ao invés de limpar
        const currentValue = getIn(formik.values, label);
        formik.setFieldValue(label, currentValue);
        return;
      }
    }

    if (mask === 'cep') {
      value = mascaraCep(value);
      formik.setFieldValue(label, value);
      return;
    }

    if (mask === 'telefone') {
      value = mascaraTelefone(value as string);
      formik.setFieldValue(label, value);
      return;
    }

    if (mask === 'numero' || mask === 'numerov2') {
      // Se acceptFraction for true, permite vírgulas e pontos
      if (acceptFraction) {
        const regex = /^[0-9]*[.,]?[0-9]*$/;
        if (!regex.test(value as string)) {
          return;
        }
      }

      if (Number(value) < 0) {
        if (typeof value === 'number') {
          value = 0;
          formik.setFieldValue(label, value);
          return;
        } else {
          value = '0';
          formik.setFieldValue(label, value);
          return;
        }
      }
      formik.setFieldValue(label, value);
      return;
    }

    if (mask === 'porcentagem') {
      let valorNumerico = Number((value as string).replace('%', ''));
      if (valorNumerico > 100) {
        valorNumerico = 100;
      }
      if (valorNumerico < 0) {
        valorNumerico = 0;
      }
      console.log('valorNumerico', valorNumerico);
      formik.setFieldValue(label, String(valorNumerico).replace('%', ''));
      return;
    }

    if (mask === 'dinheiro') {
      // Converte string para number e salva no formik
      const numeroValue = monetario_input(value as string);
      formik.setFieldValue(label, numeroValue);
      return;
    }

    if (mask === 'custom') {
      if (customMask) {
        value = customMask(value as string);
      }
      formik.setFieldValue(label, value);
      return;
    }

    if (type === 'number') {
      if (Number(value) < 0) {
        value = 0;
      }
    }

    formik.setFieldValue(label, value);
  };

  const handleGetError = () => {
    const currentValue = getIn(formik.errors, label);
    if (currentValue) {
      return currentValue;
    }
    return null;
  }

  const handleGetTouched = () => {
    return getIn(formik.touched, label);
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Bloqueia vírgulas e pontos quando acceptFraction é false
    if ((type === 'number' || mask === 'numero' || mask === 'numerov2') && !acceptFraction) {
      if (event.key === '.' || event.key === ',') {
        event.preventDefault();
        return;
      }
    }

    if (mask === 'porcentagem') {
      const currentValue = getIn(formik.values, label) || '';

      // Se for Backspace ou Delete
      if (event.key === 'Backspace' || event.key === 'Delete') {
        if (currentValue !== '' && currentValue !== null) {
          // Remove o último dígito do número
          const numeroString = currentValue.toString();
          const novoNumero = numeroString.slice(0, -1);

          if (novoNumero === '') {
            formik.setFieldValue(label, '');
          } else {
            formik.setFieldValue(label, novoNumero);
          }

          event.preventDefault();
        }
      }
    } else if (mask === 'dinheiro') {
      const currentValue = getIn(formik.values, label) || 0;

      // Se for Backspace ou Delete
      if (event.key === 'Backspace' || event.key === 'Delete') {
        if (currentValue > 0) {
          // Remove o último dígito (divide por 10 e pega a parte inteira)
          const novoValor = Math.floor(currentValue * 100 / 10) / 100;
          formik.setFieldValue(label, novoValor);
          event.preventDefault();
        }
      }
    }
  };


  return (
    <>
      {version === 'primary' ? (
        <Box sx={{ width: width }}>
          <TextField
            id='beneficiarios-exclusivamente-municipio'
            label={labelTexto}
            variant='outlined'
            placeholder={placeholder}
            type={type}
            required={required}
            value={getDisplayValue()}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            InputProps={{
              readOnly: disabled
            }}
            error={
              (formik.touched[label] && Boolean(formik.errors[label]))
              || handleGetError()
            }
            helperText={handleGetError()}
            inputProps={{
              maxLength: maxLength,
              step: acceptFraction ? '0.01' : '1',
            }}
            InputLabelProps={{
              shrink: true,
            }}
            sx={{
              width: '100%',
            }}
          />
        </Box>
      ) : (
        <FormControl
          sx={{
            width: width,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <FormLabel
            id={`${label}-label`}
            htmlFor={`${label}-input`}
            sx={{
              color: '#333',
              fontWeight: 'bold',
            }}
          >
            {required && (
              <span
                style={{ color: 'red', fontSize: '15px', marginRight: '4px' }}
              >
                *
              </span>
            )}
            {labelTexto}
          </FormLabel>

          <TextField
            id={`${label}-input`}
            variant='outlined'
            type={type}
            required={required}
            value={getDisplayValue()}
            disabled={disabled}
            inputProps={{ maxLength: maxLength }}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            error={handleGetTouched() && Boolean(handleGetError())}
            helperText={handleGetTouched() && handleGetError()}
          />
        </FormControl>
      )}
    </>
  );
};

export default InputComLabelFormik;
